// ============================================================
// EsignStatus.tsx — B4c batch 1b: an issued statement's online-signing status
// (pilot only; CaseStatements.esign[statementId] — the latest request):
//   • signed → "Signed online by Mr John Smith on … — View signed PDF ·
//     Evidence pack" (+ the answer; "sealing…" until the seal is on);
//   • open → sent / opened / code verified, the link's expiry, with Resend
//     link (a NEW link — the old one stops working) and Cancel (a reason is
//     required);
//   • declined / expired / cancelled → what happened, in plain English.
// The new ActionBox shell; the cancel reason is its white form section.
// (E-sign fixes B)
//   • "Signed — sealing…" while the sealed copy is being made (e.sealing) —
//     the panel refreshes live (hooks/useCaseLive), so it turns into the
//     normal signed view by itself;
//   • a typed name that doesn't match the record is shown plainly to the fee
//     earner, and (briefly) how the signer confirmed reading it;
//   • the pilot switched off → no buttons that would only fail; says why;
//   • an e-sign action's refusal (ESIGN_OFF …) shows here, under the status.
// (D12)
//   • a decline says WHICH: "asked to sign on paper" (with "Send by post or
//     by hand…" — the Send window opens on Posted or handed over), "Someone
//     who opened the link said they aren't …" (check the email address, then
//     send a new request) or a plain "chose not to sign online" — who, when,
//     and their reason;
//   • "Redacted pack" beside the evidence pack (?redacted=1): IP addresses,
//     devices and reasons removed — for sending to the other side.
// (Big-audit fix batch 1)
//   • the pack buttons explain themselves: a hover / focus tip that is also
//     the button's description ("Evidence pack: everything a court needs …" /
//     "Redacted pack: the proof without personal details …"); the second is
//     "Redacted pack (no personal details)", and its note says it leaves out
//     the sealed PDF and certificate (fingerprints only);
//   • once every signer has signed nothing can be cancelled or resent (the
//     server refuses with a 409 — its message shows under the status): no
//     Cancel / Resend, "Signed — sealing…" while the seal is on its way and
//     "Signed — sealing will be retried automatically" after a failed seal;
//   • a request signed on an EARLIER statement version (a correction no longer
//     cancels it) → "Signed on an earlier version (version N) — on <date>",
//     still with its signed PDF and evidence packs (`earlierVersion`).
// ============================================================
import React, { useId, useState } from 'react'
import ActionBox, { B_SM_DANGER, ErrorNote, LINK, type ActionBoxAction } from '../../shared/ActionBox'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import { BIcon, INPUT, money } from '../../billing/billingUi'
import { ukDate, ukDateTime } from '../../esign/esignFormat'
import type { EsignReadMethod, StatementEsignSummary } from '../../../types/esign.types'

export const ESIGN_STATUS_LABEL: Record<StatementEsignSummary['status'], string> = {
  SENT: 'Sent', OPENED: 'Opened', CODE_VERIFIED: 'Code verified', SIGNED: 'Signed', DECLINED: 'Declined', EXPIRED: 'Expired', CANCELLED: 'Cancelled',
}
const OPEN = ['SENT', 'OPENED', 'CODE_VERIFIED']
// (Big-audit fix batch 1) A completed request (every signer signed) is never "open".
export const isOpenEsign = (e: StatementEsignSummary | null | undefined): boolean => !!e && OPEN.includes(e.status) && e.requestStatus !== 'COMPLETED'
// (E-sign fixes B) Signed, the sealed copy not ready yet. An older server
// without `sealing` → judged from the seal status.
export const isSealing = (e: StatementEsignSummary | null | undefined): boolean =>
  !!e && (e.status === 'SIGNED' || e.requestStatus === 'COMPLETED') && (e.sealing === true || (e.sealing === undefined && e.sealStatus !== 'SEALED' && e.sealStatus !== 'FAILED'))
// Still moving (worth the fallback poll): sent / opened / code verified / sealing.
export const isEsignInProgress = (e: StatementEsignSummary | null | undefined): boolean => isOpenEsign(e) || isSealing(e)

export const signedLine = (e: StatementEsignSummary): string =>
  `Signed online by ${e.signer?.name ?? 'the signer'}${e.signer?.onBehalfOf ? ` (litigation friend for ${e.signer.onBehalfOf})` : ''}${e.signer?.signedAt ? ` on ${ukDateTime(e.signer.signedAt)}` : ''}`
export const answerLine = (e: StatementEsignSummary): string | null => {
  const s = e.signer
  if (!s?.answer) return null
  return s.answer === 'AGREED' ? 'Agreed the statement.' : `Disputed ${money(s.disputedPence ?? 0)}${s.disputeReason ? ` — “${s.disputeReason}”` : ''}.`
}
// (E-sign fixes B) The signer confirmed signing with a name that doesn't match the record.
export const nameMismatchLine = (e: StatementEsignSummary): string | null => {
  const m = e.signer?.nameMismatch
  return m ? `Signed as “${m.typed}” — the name we have is ${m.onRecord}.` : null
}
// …and how they confirmed reading the document.
export const READ_METHOD_LINE: Record<EsignReadMethod, string> = {
  SCROLL: 'Read on screen to the last page.',
  BUTTON: 'Confirmed reading with “I’ve read the whole document”.',
  DOWNLOAD: 'Read a downloaded copy and confirmed reading every page.',
}
export const ESIGN_OFF_SIGNED = 'E-signature is switched off for your firm, so the signed PDF and evidence pack can’t be opened here. An admin can turn it on in Settings → E-signature.'
export const ESIGN_OFF_OPEN = 'E-signature is switched off for your firm, so the link can’t be resent or cancelled here. An admin can turn it on in Settings → E-signature.'

// The steps so far, as short facts: "Sent 1 Nov 10:02 by Outlook · Opened 10:14 · Code entered 10:15".
export const progressLine = (e: StatementEsignSummary): string => {
  const parts = [`Sent ${e.sentAt ? ukDateTime(e.sentAt) : ''}${e.sentVia === 'SYSTEM' ? ' (from Casevo’s mailbox — your Outlook isn’t connected)' : ''}`.trim()]
  if (e.signer?.openedAt) parts.push(`opened ${ukDateTime(e.signer.openedAt)}`)
  if (e.signer?.verifiedAt) parts.push(`code entered ${ukDateTime(e.signer.verifiedAt)}`)
  return parts.join(' · ')
}

// (D12) The redacted pack, in one line.
// (Big-audit fix batch 1) …and what each pack is for (tip + description).
export const EVIDENCE_PACK_LABEL = 'Evidence pack'
export const REDACTED_PACK_LABEL = 'Redacted pack (no personal details)'
export const EVIDENCE_PACK_TIP = 'Evidence pack: everything a court needs, including personal details'
// (Big-audit fix batch 2) What the redacted pack leaves out lives in its tip
// (hover / focus, and the button's description) — no note inside the box.
// The backend's redacted pack has no 01 signed.pdf / 02 completion-certificate.pdf /
// 03 original.pdf — fingerprints only.
export const REDACTED_PACK_TIP = 'Redacted pack: the proof without personal details, for the other side, an insurer or an auditor. Leaves out the signed PDF, the certificate and the original statement (fingerprints only), and replaces addresses, devices, reasons and contact details with [redacted].'
// Every signer has signed (the request is complete): nothing to cancel or resend.
export const isFullySigned = (e: StatementEsignSummary | null | undefined): boolean => !!e && (e.status === 'SIGNED' || e.requestStatus === 'COMPLETED')
// Signed, but the seal failed — the hourly scan tries again by itself.
export const isSealRetrying = (e: StatementEsignSummary | null | undefined): boolean => isFullySigned(e) && e!.sealStatus === 'FAILED'
const signedOn = (e: StatementEsignSummary): string | null => {
  const at = e.signer?.signedAt ?? e.completedAt
  return at ? ukDate(at) : null
}

// (D12) A declined request, by what the signer pressed.
export const paperLine = (e: StatementEsignSummary): string =>
  `${e.signer?.name ?? 'The signer'} asked to sign on paper${e.signer?.declinedAt ? ` on ${ukDate(e.signer.declinedAt)}` : ''} — send it by post or by hand`
export const notMeLine = (e: StatementEsignSummary): string =>
  `Someone who opened the link said they aren’t ${e.signer?.name ?? 'the signer'}`
export const NOT_ME_NEXT = 'Check the email address, then send a new request.'
const reasonText = (r: string | null | undefined): string => (r && r.trim() ? ` They said: “${r.trim()}”.` : '')

const OffNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="m-0 mt-[6px] text-[12.5px] leading-[1.45] text-casevo-text-soft" data-esign-off="">{children}</p>
)

const EsignStatus: React.FC<{
  e: StatementEsignSummary
  busy: string | null
  onResend: () => void
  onCancel: (reason: string) => Promise<boolean>
  onViewSigned: () => void
  onEvidencePack: () => void
  // (D12) The redacted evidence pack; the Send window on post / hand, or on e-signature.
  onRedactedPack?: () => void
  onSendPaper?: () => void
  onSendNew?: () => void
  // (E-sign fixes B) The pilot is off: show what happened, offer nothing that would fail.
  pilotOff?: boolean
  // The last e-sign action's refusal (the server's reason).
  error?: string | null
  // (Big-audit fix batch 1) The request belongs to this EARLIER version of the statement.
  earlierVersion?: number
}> = ({ e, busy, onResend, onCancel, onViewSigned, onEvidencePack, onRedactedPack, onSendPaper, onSendNew, pilotOff = false, error = null, earlierVersion }) => {
  const id = useId()
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const [tried, setTried] = useState(false)
  const who = e.signer?.name ?? 'the signer'
  const errorNote = error ? <ErrorNote>{error}</ErrorNote> : null

  // Every signer has signed: never Cancel / Resend (the server refuses them too).
  if (isFullySigned(e)) {
    const retrying = isSealRetrying(e)
    const sealing = isSealing(e) && !retrying
    const sealed = e.sealStatus === 'SEALED' && !sealing && !retrying
    const pk = (redacted: boolean): ActionBoxAction => {
      const key = redacted ? 'esign:pack-redacted' : 'esign:pack'
      return {
        label: redacted ? REDACTED_PACK_LABEL : EVIDENCE_PACK_LABEL, tip: redacted ? REDACTED_PACK_TIP : EVIDENCE_PACK_TIP,
        kind: 'link', icon: 'dl', onClick: redacted ? onRedactedPack! : onEvidencePack, busy: busy === key, busyLabel: 'Making the pack…', disabled: !!busy && busy !== key,
      }
    }
    const actions: ActionBoxAction[] = sealed && !pilotOff
      ? [pk(false), ...(onRedactedPack ? [pk(true)] : []),
        { label: 'View signed PDF', kind: 'link', icon: 'eye', onClick: onViewSigned, busy: busy === 'esign:pdf', busyLabel: 'Opening…', disabled: !!busy && busy !== 'esign:pdf' }]
      : []
    const mismatch = nameMismatchLine(e)
    const read = e.signer?.readMethod ? READ_METHOD_LINE[e.signer.readMethod] : null
    const answer = <>{answerLine(e)}{e.answerRecorded ? ' Recorded as the client’s answer (e-signature).' : e.signer?.onBehalfOf ? ' The court still approves the deductions.' : ''}</>
    const on = signedOn(e)
    const sealNote = sealing ? ' We’re sealing the signed PDF — this usually takes a minute and updates here by itself.'
      : retrying ? ' Sealing the signed PDF didn’t work this time — Casevo tries again automatically, and this updates by itself.'
        : sealed ? '' : ' The signed PDF isn’t sealed yet.'
    const title = earlierVersion !== undefined ? `Signed on an earlier version (version ${earlierVersion})${on ? ` — on ${on}` : ''}`
      : sealing ? 'Signed — sealing…'
        : retrying ? 'Signed — sealing will be retried automatically'
          : signedLine(e)
    const message = earlierVersion !== undefined
      ? <>{signedLine(e)}. {answer} The signature belongs to version {earlierVersion}; this version hasn’t been signed online.{sealNote}</>
      : sealing || retrying ? <>{signedLine(e)}. {answer}{sealNote}</>
        : <>{answer}{sealNote}</>
    return (
      <div data-esign-status="SIGNED" data-sealing={sealing ? '' : undefined} data-seal-retrying={retrying ? '' : undefined}
        data-earlier-version={earlierVersion} className="flex flex-col gap-[8px]">
        <ActionBox tone={earlierVersion !== undefined ? 'info' : 'done'} live={earlierVersion === undefined}
          ariaLabel={earlierVersion !== undefined ? 'Signed on an earlier version' : sealing ? 'Signed, sealing' : retrying ? 'Signed, sealing will be retried' : 'Signed online'}
          title={title} message={message} actions={actions}>
          {(mismatch || read) && (
            <ul className="m-0 mt-[6px] pl-0 list-none flex flex-col gap-[4px] text-[12.5px] leading-[1.45]" data-signing-details="">
              {mismatch && (
                <li className="flex items-start gap-[6px] text-casevo-warn font-medium" data-name-mismatch="">
                  <BIcon name="alert" size={13} className="mt-[2px]" />
                  <span><span className="sr-only">Check: </span>{mismatch} They confirmed this was right when signing.</span>
                </li>
              )}
              {read && <li className="text-casevo-text-soft" data-read-method={e.signer?.readMethod ?? ''}>{read}</li>}
            </ul>
          )}
          {pilotOff && sealed && <OffNote>{ESIGN_OFF_SIGNED}</OffNote>}
        </ActionBox>
        {errorNote}
      </div>
    )
  }
  if (OPEN.includes(e.status)) {
    const reasonErr = tried && !reason.trim() ? 'Say why it’s being cancelled.' : null
    return (
      <div data-esign-status={e.status} className="flex flex-col gap-[8px]">
        <ActionBox tone="info" ariaLabel="Sent for e-signature"
          title={`Sent for e-signature to ${who} — ${ESIGN_STATUS_LABEL[e.status].toLowerCase()}`}
          message={<>{progressLine(e)}.{e.signer?.linkExpiresAt ? ` The link works until ${ukDateTime(e.signer.linkExpiresAt)}.` : ''}</>}
          actions={cancelling || pilotOff ? [] : [
            { label: 'Cancel request', kind: 'link', onClick: () => setCancelling(true), disabled: !!busy },
            { label: 'Resend link', kind: 'link', icon: 'send', onClick: onResend, busy: busy === 'esign:resend', busyLabel: 'Resending…', disabled: !!busy && busy !== 'esign:resend' },
          ]}
          form={cancelling && !pilotOff ? (
            <div className="flex flex-col gap-[10px]">
              <FormField label="Why are you cancelling it?" required variant="info" htmlFor={`${id}-reason`}>
                <input id={`${id}-reason`} className={INPUT} value={reason} maxLength={500} onChange={(ev) => setReason(ev.target.value)}
                  aria-invalid={!!reasonErr || undefined} aria-describedby={`${id}-reason-err`} />
                <span id={`${id}-reason-err`}>{reasonErr && <FieldError message={reasonErr} />}</span>
              </FormField>
              <p className="m-0 text-[12.5px] text-casevo-text-soft">The link stops working at once. {who} will see that it was cancelled.</p>
              <div className="flex items-center gap-[10px] justify-end">
                <button type="button" className={LINK} onClick={() => { setCancelling(false); setReason(''); setTried(false) }}>Keep it</button>
                <button type="button" data-kind="danger"
                  className={B_SM_DANGER}
                  disabled={busy === 'esign:cancel'}
                  onClick={() => { setTried(true); if (!reason.trim()) return; void onCancel(reason.trim()).then((ok) => { if (ok) { setCancelling(false); setReason(''); setTried(false) } }) }}>
                  {busy === 'esign:cancel' ? 'Cancelling…' : 'Cancel the request'}
                </button>
              </div>
            </div>
          ) : undefined}>
          {pilotOff && <OffNote>{ESIGN_OFF_OPEN}</OffNote>}
        </ActionBox>
        {errorNote}
      </div>
    )
  }
  if (e.status === 'DECLINED') {
    const kind = e.signer?.declineKind ?? 'DECLINE'
    const when = e.signer?.declinedAt ? ukDateTime(e.signer.declinedAt) : null
    const reason = reasonText(e.signer?.declineReason)
    const box = kind === 'PAPER'
      ? <ActionBox tone="warn" live ariaLabel="Asked to sign on paper" title={paperLine(e)}
        message={<>They pressed “I’d rather sign on paper” on the signing page{when ? ` (${when})` : ''}.{reason} The online link has stopped working.</>}
        actions={onSendPaper ? [{ label: 'Send by post or by hand…', kind: 'link', icon: 'send', onClick: onSendPaper, disabled: !!busy }] : undefined} />
      : kind === 'NOT_ME'
        ? <ActionBox tone="danger" live ariaLabel="Opened by someone else" title={notMeLine(e)}
          message={<>{when ? `On ${when}. ` : ''}{NOT_ME_NEXT}{reason} The link has stopped working.</>}
          actions={onSendNew && !pilotOff ? [{ label: 'Send a new request…', kind: 'link', icon: 'send', onClick: onSendNew, disabled: !!busy }] : undefined} />
        : <ActionBox tone="warn" live ariaLabel="Chose not to sign online" title={`${who} chose not to sign online`}
          message={<>{when ? `On ${when}.` : ''}{reason} Contact them, then send it again (Send…) or record their answer.</>} />
    return <div data-esign-status="DECLINED" data-decline-kind={kind} className="flex flex-col gap-[8px]">{box}{errorNote}</div>
  }
  const t = e.status === 'EXPIRED'
      ? { tone: 'warn' as const, title: 'The signing link expired', message: `${who} didn’t sign before the link ran out. Send it again (Send…) if they still need to.` }
      : { tone: 'info' as const, title: 'E-signature request cancelled', message: `${e.revokedAt ? `${ukDateTime(e.revokedAt)}. ` : ''}${e.revokeReason ? `“${e.revokeReason}”` : ''}` }
  return <div data-esign-status={e.status} className="flex flex-col gap-[8px]"><ActionBox tone={t.tone} title={t.title} message={t.message} />{errorNote}</div>
}

// (Big-audit fix batch 2) Once the CURRENT version is signed, the requests
// signed on earlier versions fold into one slim "Earlier signings (N)" line
// that opens to show their cards (packs, signed PDFs, any name to check).
// A name mismatch inside is flagged on the folded line too, so it's never
// hidden.
export const earlierSigningsLabel = (n: number): string => `Earlier signings (${n})`
export const EARLIER_MISMATCH_MARKER = 'A name to check'
export const EarlierSignings: React.FC<{
  count: number
  versions: number[]
  // At least one of them was signed with a name that doesn't match the record.
  mismatch: boolean
  children: React.ReactNode
}> = ({ count, versions, mismatch, children }) => {
  const id = useId()
  const [open, setOpen] = useState(false)
  const which = versions.length ? `${versions.length === 1 ? 'Version' : 'Versions'} ${versions.join(', ')}` : ''
  return (
    <div data-earlier-signings={count} data-open={open ? '' : undefined} className="flex flex-col gap-[8px]">
      <button type="button" aria-expanded={open} aria-controls={`${id}-list`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-[8px] flex-wrap text-left rounded-[10px] border border-casevo-border bg-casevo-surface px-[12px] py-[8px] min-h-[40px] text-[13px] hover:bg-casevo-input focus:outline-none casevo-focus">
        <BIcon name="chev" size={13} className={`text-casevo-text-soft transition-transform motion-reduce:transition-none ${open ? '' : '-rotate-90'}`} />
        <span className="font-semibold text-casevo-forest">{earlierSigningsLabel(count)}</span>
        {which && <span className="text-casevo-text-soft text-[12.5px]">{which} — signed online</span>}
        {mismatch && (
          <span className="ml-auto inline-flex items-center gap-[4px] rounded-full border border-casevo-warn-border bg-casevo-warn-bg px-[8px] py-[1px] text-[12px] font-medium text-casevo-warn" data-earlier-mismatch="">
            <BIcon name="alert" size={12} />{EARLIER_MISMATCH_MARKER}
          </span>
        )}
      </button>
      <div id={`${id}-list`} hidden={!open} className="flex flex-col gap-[8px]">
        {open && children}
      </div>
    </div>
  )
}

export default EsignStatus
